import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  Feature,
  getAddOnCodeByFeature,
  getFeatureFromPlan,
  isTestCloudFeature,
  isTestOpsPlatformFeature,
  isVisualTestingFeature,
  Order,
  OrderItem,
  OrderStatus,
  PlanInterval,
} from '../../models';
import { formatUSPrice } from '../../utils/utils';
import { fromOrder } from '../../store';
import { createFeatures } from '../../models/FeatureConfig';
import { formatCurrency } from '../../components';
import { DEFAULT_CURRENCY_FORMAT_OPTIONS } from '../../utils/Constants';
import { getItemPayAmount } from './orderAmount';
import { StatusPill, StatusPillVariant } from './StatusPill';

const useStyles = makeStyles(theme => ({
  name: {
    fontFamily: "'Inter Tight', sans-serif",
    fontSize: 15,
    fontWeight: 600,
    letterSpacing: '-.01em',
    margin: theme.spacing(0),
    color: '#16141D',
  },
  headCell: {
    padding: theme.spacing(1.75, 2),
    maxWidth: theme.spacing(33),
    '&.MuiTableCell-root': {
      paddingBottom: theme.spacing(1.5),
      borderBottom: '1px solid #ECECF4',
      fontSize: 11.5,
      fontWeight: 700,
      letterSpacing: '.06em',
      textTransform: 'uppercase',
      color: '#8A88A0',
    },
  },
  tableInnerCell: {
    padding: theme.spacing(2, 2),
    maxWidth: theme.spacing(33),
    fontSize: 13.5,
    color: '#5F5D75',
    verticalAlign: 'top',
    '&.MuiTableCell-root': {
      borderBottom: '1px solid #F3F2F8',
    },
  },
  cycleSubscription: {
    fontWeight: 500,
  },
  tableInnerCellPricing: {
    padding: theme.spacing(2, 2),
    maxWidth: theme.spacing(33),
    fontSize: 14.5,
    color: '#16141D',
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
    verticalAlign: 'top',
    textAlign: 'right',
    '&.MuiTableCell-root': {
      borderBottom: '1px solid #F3F2F8',
    },
  },
  addonItemName: {
    fontSize: 13,
    color: '#5B6661',
    lineHeight: '200%',
  },
  addonItemWrapper: {
    borderLeft: '2px solid #E1E7E4',
    paddingLeft: theme.spacing(1.5),
    marginLeft: theme.spacing(2),
    marginTop: theme.spacing(0.5),
  },
  amountTotal: {
    fontWeight: 700,
    fontSize: 16,
    color: '#16141D',
    fontVariantNumeric: 'tabular-nums',
  },
  amountBreakdownItem: {
    fontSize: 12,
    color: '#73718A',
    textAlign: 'right',
    lineHeight: '180%',
  },
  productQuantity: {
    fontSize: 13,
    color: '#5B6661',
    marginTop: theme.spacing(0.25),
  },
  orderTotalRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'flex-end',
    gap: 40,
    padding: '18px 16px 0',
  },
  orderTotalLabel: {
    fontSize: 13.5,
    fontWeight: 600,
    color: '#73718A',
  },
  orderTotalValue: {
    fontFamily: "'Inter Tight', sans-serif",
    fontSize: 22,
    fontWeight: 700,
    color: '#16141D',
    fontVariantNumeric: 'tabular-nums',
    minWidth: 130,
    textAlign: 'right',
  },
}));

const isLegacyTruePlatformOrderItem = (item: OrderItem) => {
  const feature = getFeatureFromPlan(item.planId);
  return feature === Feature.TRUE_TEAM;
};

const OrderTable = (
  { order }: { order?: Order },
) => {
  const intl = useIntl();
  const classes = useStyles();
  const errors = useSelector(fromOrder.selectErrors);
  const { enqueueSnackbar } = useSnackbar();
  const features = useMemo(() => createFeatures(), []);
  useEffect(() => {
    if (errors && errors[0]) {
      enqueueSnackbar(
        <FormattedMessage id="order_fetch_error" />,
        {
          variant: 'error',
          preventDuplicate: true,
          key: intl.formatMessage({ id: 'order_fetch_error' }),
          autoHideDuration: 2000,
          resumeHideDuration: 0,
        },
      );
    }
  }, [errors]);

  const getPlanIntervalName = (billingCycle: PlanInterval) => {
    switch (billingCycle) {
      case PlanInterval.MONTH:
        return intl.formatMessage({ id: 'usage_plan_interval_monthly' });
      case PlanInterval.YEAR:
        return intl.formatMessage({ id: 'usage_plan_interval_yearly' });
      default:
        return '';
    }
  };

  const getOrderStatusChip = (orderStatus: OrderStatus | undefined) => {
    let variant: StatusPillVariant;
    let labelId: string;
    switch (orderStatus) {
      case OrderStatus.PAID:
      case OrderStatus.FULLY_RECEIVED:
        variant = 'paid';
        labelId = 'payment_status.order_status.completed';
        break;
      case OrderStatus.FAIL:
      case OrderStatus.CANCEL:
        variant = 'failed';
        labelId = 'invoice_state.failed';
        break;
      case OrderStatus.PENDING:
      case OrderStatus.PASS_DUE:
      case OrderStatus.PARTIALLY:
        variant = 'processing';
        labelId = 'invoice_state.processing';
        break;
      default:
        return (
          <span>
            <b>{intl.formatMessage({ id: 'invoice_state.unknown' })}</b>
          </span>
        );
    }
    return <StatusPill variant={variant} label={intl.formatMessage({ id: labelId })} />;
  };

  const getProductUnit = (orderItem: OrderItem, feature: Feature | undefined) => {
    if (orderItem.type === 'STARTER_PACKAGE') {
      return intl.formatMessage({ id: 'order_item.unit.package_items' });
    }
    if (feature && isTestCloudFeature(feature)) {
      return intl.formatMessage({ id: 'order_item.unit.sessions' });
    }
    if (feature && isVisualTestingFeature(feature)) {
      return intl.formatMessage({ id: 'order_item.unit.checkpoint_images' });
    }
    if (feature && isTestOpsPlatformFeature(feature)) {
      return intl.formatMessage({ id: 'order_item.unit.test_results' });
    }
    return intl.formatMessage({ id: 'order_item.unit.seats' });
  };

  const getItemDisplayName = (orderItem: OrderItem) => {
    const feature = getFeatureFromPlan(orderItem.planId);
    const featureConfig = features.find(f => f.feature === feature);
    return featureConfig
      ? intl.formatMessage({ id: featureConfig.planName })
      : (orderItem.productName || orderItem.planId || 'Product');
  };

  const getItemShortName = (orderItem: OrderItem) => {
    const feature = getFeatureFromPlan(orderItem.planId);
    if (!feature) {
      return getItemDisplayName(orderItem);
    }
    const shortNameKey = `feature.${feature.toLowerCase()}.product.short.name`;
    // Use defaultMessage to safely fall back when the key doesn't exist
    const shortName = intl.formatMessage({ id: shortNameKey, defaultMessage: '' });
    return shortName !== '' ? shortName : getItemDisplayName(orderItem);
  };

  const getAddonDisplayName = (orderItem: OrderItem) => {
    const feature = getFeatureFromPlan(orderItem.planId);
    const addOnCode = feature ? getAddOnCodeByFeature(feature) : undefined;
    return addOnCode
      ? intl.formatMessage({ id: `addon.${addOnCode}.display.name` })
      : getItemDisplayName(orderItem);
  };

  const renderDescriptionCell = (orderItem: OrderItem) => (
    <TableCell className={classes.tableInnerCell}>
      {!orderItem ? <Skeleton width="50%" /> : intl.formatMessage(
        { id: 'payment_success.order_summary.table.plan_interval' },
        {
          planName: (
            <span className={classes.cycleSubscription}>
              {getPlanIntervalName(orderItem.billingCycle)}
            </span>
          ),
          br: <br />,
          startDate: orderItem.subscriptionStartDate
            ? intl.formatDate(new Date(orderItem.subscriptionStartDate), {
              month: 'short',
              day: '2-digit',
              year: 'numeric',
            })
            : '-',
          endDate: orderItem.subscriptionEndDate
            ? intl.formatDate(new Date(orderItem.subscriptionEndDate), {
              month: 'short',
              day: '2-digit',
              year: 'numeric',
            })
            : '-',
        },
      )}
    </TableCell>
  );

  /** Render a single normal (non-TruePlatform) item row */
  const generateOrderItemRow = (ord: Order, orderItem: OrderItem) => {
    const feature = getFeatureFromPlan(orderItem.planId);
    const displayName = getItemDisplayName(orderItem);

    return (
      <TableRow key={`${ord.id}-${orderItem.id}`}>
        <TableCell className={classes.tableInnerCell}>
          {!orderItem ? <Skeleton width="50%" /> : (
            <>
              <p className={classes.name}>
                {displayName}
              </p>
              {intl.formatMessage(
                { id: 'payment_success.order_summary.table.product_info' },
                {
                  quantity: formatUSPrice(orderItem.quantity),
                  unit: getProductUnit(orderItem, feature),
                },
              )}
            </>
          )}
        </TableCell>
        {renderDescriptionCell(orderItem)}
        <TableCell className={classes.tableInnerCellPricing}>
          {!orderItem
            ? <Skeleton width="50%" />
            : formatCurrency(
              intl,
              getItemPayAmount(orderItem),
              DEFAULT_CURRENCY_FORMAT_OPTIONS,
            )}
        </TableCell>
        <TableCell className={classes.tableInnerCell}>
          {!ord ? <Skeleton width="50%" /> : getOrderStatusChip(ord.status)}
        </TableCell>
      </TableRow>
    );
  };

  /**
   * Render a grouped row for a True Platform item with its addon sub-items.
   * The product cell lists the main item then the addon names indented below.
   * The amount cell shows the combined total with a per-item breakdown.
   */
  const generateTruePlatformGroupedRow = (
    ord: Order,
    truePlatformItem: OrderItem,
    addonItems: OrderItem[],
  ) => {
    const feature = getFeatureFromPlan(truePlatformItem.planId);
    const displayName = getItemDisplayName(truePlatformItem);
    const unit = getProductUnit(truePlatformItem, feature);

    const totalAmount = [...addonItems, truePlatformItem].reduce(
      (sum, item) => sum + getItemPayAmount(item),
      0,
    );

    const truePlatformShortName = getItemShortName(truePlatformItem);
    const truePlatformItemAmount = getItemPayAmount(truePlatformItem);

    return (
      <TableRow key={`${ord.id}-${truePlatformItem.id}-grouped`}>
        {/* Product column: main item + indented addon names */}
        <TableCell className={classes.tableInnerCell}>
          {!truePlatformItem ? <Skeleton width="50%" /> : (
            <>
              <p className={classes.name}>{displayName}</p>
              <p className={classes.productQuantity}>
                {`${formatUSPrice(truePlatformItem.quantity)} ${unit}`}
              </p>
              {addonItems.length > 0 && (
                <Box className={classes.addonItemWrapper}>
                  {addonItems.map(addon => (
                    <Typography
                      key={`addon-name-${addon.id}`}
                      className={classes.addonItemName}
                    >
                      {getAddonDisplayName(addon)}
                    </Typography>
                  ))}
                </Box>
              )}
            </>
          )}
        </TableCell>

        {/* Description column: billing cycle of the True Platform item */}
        {renderDescriptionCell(truePlatformItem)}

        {/* Amount column: total + per-item breakdown */}
        <TableCell className={classes.tableInnerCellPricing}>
          {!truePlatformItem ? <Skeleton width="50%" /> : (
            <>
              <Typography className={classes.amountTotal}>
                {formatCurrency(intl, totalAmount, DEFAULT_CURRENCY_FORMAT_OPTIONS)}
              </Typography>
              <Typography className={classes.amountBreakdownItem}>
                {intl.formatMessage(
                  { id: 'payment_success.order_summary.breakdown.platform_item' },
                  {
                    shortName: truePlatformShortName,
                    quantity: formatUSPrice(truePlatformItem.quantity),
                    unit,
                    price: formatCurrency(
                      intl,
                      truePlatformItemAmount,
                      DEFAULT_CURRENCY_FORMAT_OPTIONS,
                    ),
                  },
                )}
              </Typography>
              {addonItems.map(addon => {
                const addonAmount = getItemPayAmount(addon);
                return (
                  <Typography
                    key={`addon-amount-${addon.id}`}
                    className={classes.amountBreakdownItem}
                  >
                    {intl.formatMessage(
                      { id: 'payment_success.order_summary.breakdown.addon_item' },
                      {
                        shortName: getAddonDisplayName(addon),
                        price: formatCurrency(intl, addonAmount, DEFAULT_CURRENCY_FORMAT_OPTIONS),
                      },
                    )}
                  </Typography>
                );
              })}
            </>
          )}
        </TableCell>

        {/* Status column */}
        <TableCell className={classes.tableInnerCell}>
          {!ord ? <Skeleton width="50%" /> : getOrderStatusChip(ord.status)}
        </TableCell>
      </TableRow>
    );
  };

  const generateOrderRows = () => {
    if (!order?.orderItems) return [];

    const truePlatformItem = order.orderItems.find(isLegacyTruePlatformOrderItem);
    if (truePlatformItem) {
      const addonItems = order.orderItems.filter(item => item.id !== truePlatformItem.id);
      return [generateTruePlatformGroupedRow(order, truePlatformItem, addonItems)];
    }

    return order.orderItems.map(orderItem => generateOrderItemRow(order, orderItem));
  };

  const orderTotal = (order?.orderItems ?? []).reduce(
    (sum, item) => sum + getItemPayAmount(item),
    0,
  );

  return (
    <TableContainer id="order-table">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headCell}>
              {intl.formatMessage({ id: 'payment_success.order_summary.table.product' })}
            </TableCell>
            <TableCell className={classes.headCell}>
              {intl.formatMessage({
                id: 'payment_success.order_summary.table.description',
              })}
            </TableCell>
            <TableCell className={classes.headCell} sx={{ textAlign: 'right' }}>
              {intl.formatMessage({ id: 'payment_success.order_summary.table.amount' })}
            </TableCell>
            <TableCell className={classes.headCell}>
              {intl.formatMessage({ id: 'payment_success.order_summary.table.status' })}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {generateOrderRows()}
        </TableBody>
      </Table>
      {order?.orderItems && order.orderItems.length > 0 && (
        <div className={classes.orderTotalRow}>
          <span className={classes.orderTotalLabel}>
            {intl.formatMessage({ id: 'payment_status.order_summary.total' })}
          </span>
          <span className={classes.orderTotalValue}>
            {formatCurrency(intl, orderTotal, DEFAULT_CURRENCY_FORMAT_OPTIONS)}
          </span>
        </div>
      )}
    </TableContainer>
  );
};

export default OrderTable;
